import { memo, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useRouter } from 'next/router';

import { Box, FormHelp, Icon, Label, PropertyCard, Text, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { IconDeleteOutline, IconFloorPlanOutline } from '@zillow/constellation-icons';

import { shimmerStyles } from '@zg-rentals/loading-components';
import { photoUtils } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserPropertyFloorPlanLayoutV2WebApiHandler_delete,
  useRentalManagerUserPropertyFloorPlanLayoutWebApiHandler_upload,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import { getPhotoUrls } from '../../../utils/propertiesPageUtils';
import { getIsInputFormDisabled } from '../../MFAccordion/utils';

import { layoutImageContainerStyles } from './components/FloorPlanLayout/styles';

import { slowClient } from '../../../http/zgApiClient';
import { useAuth } from '../../AuthProvider';
import UploadDropzone from '../../UploadDropzone';

import type { FileRejection } from 'react-dropzone';

type Props = {
  floorPlanIndex: number;
};

const FloorPlanLayout = ({ floorPlanIndex }: Props) => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const [uploadingCount, setUploadingCount] = useState<number | null>(null);

  const floorplans = listingDetails?.floorPlans || [];
  const floorPlan = floorplans[floorPlanIndex];
  const canUpdateListing = user?.features?.canUpdateListing ?? false;
  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);
  const isRunningInProd = process.env.NODE_ENV === 'production';

  const { mutateAsync: uploadLayout } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanLayoutWebApiHandler_upload,
    successToastMessage: 'Layout uploaded',
    httpClient: slowClient,
  });

  const { mutateAsync: deleteLayout } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanLayoutV2WebApiHandler_delete,
    successToastMessage: 'Layout deleted',
  });

  const onDropAccepted = async (acceptedFiles: Array<File>) => {
    let file: File = acceptedFiles?.[0];

    if (Array.isArray(acceptedFiles)) {
      const validFiles = await photoUtils.filterForValidFiles([acceptedFiles[0]], onDropRejected);
      file = validFiles?.[0] as File;
    }

    if (!file) {
      return;
    }

    setUploadingCount(1);

    const floorPlanId = floorPlan?.id;

    return uploadLayout({
      getParams: { floorPlanId },
      postData: { sourceFile: file },
    })
      .catch((err) => {
        enqueueToast(
          <Toast tone="critical">
            {err.message || 'There was an error uploading your photo, refresh the page and try again'}
          </Toast>,
        );
      })
      .finally(() => {
        setUploadingCount(0);
      });
  };

  const onDropRejected = (
    errorData?: Array<FileRejection> | Array<{ errors?: Array<{ message: string; code?: string }> }>,
  ) => enqueueToast(<Toast tone="critical">{errorData?.[0]?.errors?.[0]?.message}</Toast>);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject, acceptedFiles } = useDropzone({
    accept: 'image/*',
    onDropAccepted,
    onDropRejected,
    disabled: (uploadingCount && uploadingCount > 0) || isInputFormDisabled,
  });

  // NOTE: `useMemo` is okay here since `rootProps` and `inputProps` are only needed once
  const rootProps = useMemo(
    () => getRootProps({ isFocused, isDragAccept, isDragReject, acceptedFiles }),
    [isFocused, isDragAccept, isDragReject, acceptedFiles, getRootProps],
  );
  const inputProps = useMemo(() => getInputProps(), [getInputProps]);

  const handleDeleteClick = () => {
    setUploadingCount(1);

    const floorPlanId = floorPlan?.id;

    return deleteLayout({ postData: { floorPlanId } })
      .catch((err) => {
        enqueueToast(
          <Toast tone="critical">{err.message || 'Layout couldn’t be deleted. Refresh and try again.'}</Toast>,
        );
      })
      .finally(() => {
        setUploadingCount(0);
      });
  };

  const renderLayoutContent = () => {
    if (floorPlan?.layout?.url || uploadingCount) {
      const { errorCode, errorMessage, replacementPhotoId } = floorPlan?.layout?.errorDto || {};

      let layoutSrc;
      if (errorCode) {
        layoutSrc = replacementPhotoId ? getPhotoUrls(isRunningInProd, replacementPhotoId).medium : '';
      } else {
        layoutSrc = floorPlan?.layout?.url;
      }

      return (
        <Box css={layoutImageContainerStyles}>
          {(uploadingCount ?? 0) > 0 && (
            <Box
              data-loading={true}
              css={{
                borderRadius: '100',
                position: 'absolute',
                width: '100%',
                height: '100%',
                ...shimmerStyles,
              }}
            />
          )}
          <PropertyCard.Photo
            src={layoutSrc || undefined}
            alt="floor plan layout"
            fallbackImg={<PropertyCard.PhotoFallback />}
            css={{
              width: '100%',
              height: '100%',
              borderRadius: '200',
              '& img': { objectFit: 'contain' },
            }}
          />
          {errorMessage && <FormHelp error={true}>{errorMessage}</FormHelp>}
        </Box>
      );
    }

    if (isInputFormDisabled) {
      return (
        <Box
          css={{ ...layoutImageContainerStyles, opacity: isInputFormDisabled ? 'action.disabled' : 'action.active' }}
        >
          <Icon render={<IconFloorPlanOutline />} size="lg" />
          <Text asChild css={{ marginTop: '400' }}>
            <p>No floor plan layout added</p>
          </Text>
        </Box>
      );
    }

    return (
      <UploadDropzone
        isDisabled={isInputFormDisabled}
        isEmpty={uploadingCount === 0}
        rootProps={rootProps}
        inputProps={inputProps}
        displayText="Drag floor plan layout image here"
        IconToDisplay={<IconFloorPlanOutline />}
      />
    );
  };

  return (
    <Box data-testid="FloorPlanLayout" css={{ display: 'flex', flexDirection: 'column', flex: '1 1 50%', gap: '200' }}>
      <Box css={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <Label disabled={isInputFormDisabled} size="lg">
          Layout
        </Label>

        {floorPlan?.layout?.url && (
          <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
            <TextButton
              tone="neutral"
              onClick={handleDeleteClick}
              icon={<IconDeleteOutline />}
              disabled={isInputFormDisabled || Boolean(uploadingCount && uploadingCount > 0)}
            >
              Remove layout
            </TextButton>
          </Box>
        )}
      </Box>

      {renderLayoutContent()}
    </Box>
  );
};

export default memo(FloorPlanLayout);
